import * as Dialog from '@radix-ui/react-dialog';
import { IconButton } from '@sds-system/components/IconButton';
import { styled } from '@sds-system/system';

import {
  getAllStyledProps,
  getBorderStyles,
  getColorStyles,
  getFlexStyles,
  getLayoutStyles,
  getOpacityStyles,
  getShadowStyles,
  getTypographyStyles,
  mediaQuery,
} from '@sds-system/utils';
import { getCSSStyles } from '@sds-system/utils/styledUtils/css';
import type {
  ModalContentStyledComponentProps,
  ModalOverlayStyledComponentProps,
  ModalRootStyledComponentProps,
  ModalTitleWrapperComponentProps,
} from './Modal.types';

export const StyledModalRoot = styled(Dialog.Content, {
  componentName: 'Modal',
  slot: 'root',
})<ModalRootStyledComponentProps>(
  ({
    theme,
    width,
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    padding,
    borderRadius,
    isResponsive,
    zIndex,
  }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: theme.palette.system.white,
    padding,
    borderRadius,
    overflow: 'hidden',
    zIndex: zIndex ? zIndex : theme.zIndex[9999],
    width,
    height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    ...(isResponsive &&
      mediaQuery(
        {
          width: width || 'calc(100% - 16px - 16px)',
          height: height || '80%',
          maxHeight,
          maxWidth,
          minHeight,
          minWidth,
        },
        theme,
      )),
  }),
  getLayoutStyles,
  getBorderStyles,
  getColorStyles,
  getOpacityStyles,
  getShadowStyles,
  getTypographyStyles,
  getFlexStyles,
  getCSSStyles,
);
export const StyledModalTitleWrapper = styled('div', {
  componentName: 'Modal',
  slot: 'title',
})<ModalTitleWrapperComponentProps>(
  {
    width: 'calc(100% - 16px)',
    marginBottom: '16px',
  },
  ({ theme, isResponsive }) => ({
    color: theme.palette.text.primary,
    ...theme.typography.body.bold,
    ...(isResponsive &&
      mediaQuery(
        {
          fontType: ['body.bold', 'header2.bold'],
        },
        theme,
      )),
  }),
  ...getAllStyledProps,
);
export const StyledModalContentWrapper = styled('div')<ModalContentStyledComponentProps>(
  ({ theme, isResponsive }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    ...theme.typography.body2.regular,
    ...(isResponsive &&
      mediaQuery(
        {
          fontSize: [
            theme.typography.body2.regular.fontSize,
            theme.typography.body.regular.fontSize,
          ],
          fontWeight: [
            theme.typography.body2.regular.fontWeight,
            theme.typography.body.regular.fontWeight,
          ],
          letterSpacing: [
            theme.typography.body2.regular.letterSpacing,
            theme.typography.body.regular.letterSpacing,
          ],
          lineHeight: [
            theme.typography.body2.regular.lineHeight,
            theme.typography.body.regular.lineHeight,
          ],
        },
        theme,
      )),
  }),
  getAllStyledProps,
);

export const StyledModalCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '16px',
  top: '16px',
  zIndex: theme.zIndex[9999],
}));

export const StyledDialogOverlay = styled(Dialog.Overlay)<ModalOverlayStyledComponentProps>(
  ({ theme, zIndex }) => ({
    backgroundColor: theme.palette.bg.dimmer[60],
    position: 'fixed',
    width: '100%',
    height: '100%',
    zIndex: zIndex ? zIndex : theme.zIndex[9999],
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,

    '~ .radix-dialog-overlay': {
      display: 'none',
    },
  }),
);
